import React, { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { adsApi, ServedAd } from '../services/adsApi';
import { GeoLocation } from '../types';

interface Props {
  slot: ServedAd['slot'];
  userLocation?: GeoLocation | null;
  /** Tighten the visual chrome for in-feed cards. */
  variant?: 'feed' | 'sidebar' | 'map-overlay';
}

/**
 * Native ad slot. Renders an embedded sponsored card if the server returns
 * an active ad for this slot + viewer; renders nothing if not (e.g. premium
 * users get `{ad: null, reason: 'premium'}` and this component disappears).
 *
 * Clicks open the campaign target via a server redirect at /api/ads/click,
 * which records the click before bouncing the user — no client-side fetch.
 */
const AdSlot: React.FC<Props> = ({ slot, userLocation, variant = 'feed' }) => {
  const [ad, setAd] = useState<ServedAd | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    adsApi.serve(slot, { lat: userLocation?.lat, lng: userLocation?.lng })
      .then(r => { if (alive) setAd(r.ad); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slot, userLocation?.lat, userLocation?.lng]);

  if (loading || !ad) return null;

  const chrome = variant === 'sidebar'
    ? 'w-full bg-slate-900/90 border border-amber-700/30 rounded p-2'
    : variant === 'map-overlay'
      ? 'absolute bottom-4 left-4 z-30 bg-slate-900/90 border border-amber-700/40 rounded p-2 max-w-xs shadow-lg'
      : 'rounded-lg border border-amber-800/30 bg-slate-900/80 backdrop-blur-sm p-3 mb-3';

  return (
    <a href={ad.clickUrl} target="_blank" rel="sponsored noopener noreferrer"
      className={`${chrome} block hover:border-amber-500/60 transition`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] uppercase tracking-widest text-amber-500/80 font-bold">Sponsored</span>
        <ExternalLink size={11} className="text-slate-500"/>
      </div>
      {ad.mediaUrl && ad.mediaKind === 'image' && (
        <img src={ad.mediaUrl} alt="" loading="lazy"
          className="rounded border border-slate-700/60 w-full max-h-44 object-cover mb-2"/>
      )}
      <div className="text-slate-100 font-semibold text-sm">{ad.name}</div>
      {ad.textCopy && <div className="text-slate-400 text-xs mt-0.5">{ad.textCopy}</div>}
    </a>
  );
};

export default AdSlot;
